// ═══ 말소대행신청 순수 로직 (B3 S2) — 앱 malso_quick_request.dart · malso_request_controller.dart 와 같은 규칙 ═══
// 화면 상태·호출 없음. POST /cancel-requests/quick body 는 **키 존재 규칙**이 계약의 핵심(서버 ownKey) —
// 공통 옵션을 쓰는 차에는 services·buyerName 키를 넣지 않는다. agencyId·companyId 는 보내지 않는다(서버 결정).

/// 카드 칸 — OCR 응답·confidence 키와 같은 이름 (앱 kMalsoCarFields).
export var CAR_FIELDS = ['carNo', 'vin', 'carType', 'regDate', 'mileage', 'owner', 'ownerId', 'ownerAddr'];
/// 말소신청서에 찍히는 칸 — 전부 필수(Jin 10/28, 앱 kMalsoRequiredFields · 서버 quick FIELD_REQUIRED). 차종·등록일은 선택.
export var REQUIRED_FIELDS = ['carNo', 'vin', 'mileage', 'owner', 'ownerId', 'ownerAddr'];
var WEAK = { low: 1, corrected: 1, no_korean: 1 };

function str(v) { return String(v == null ? '' : v).trim(); }
function has(o, k) { return !!o && Object.prototype.hasOwnProperty.call(o, k); }

/// 주행거리 — 숫자만, 0 은 빈 칸(OCR 은 못 읽으면 0 을 준다). 앱 malsoMileageText.
export function mileageText(v) {
  var d = String(v == null ? '' : v).replace(/\D/g, '');
  return Number(d) > 0 ? String(Number(d)) : '';
}

/// VIN — trim + 대문자 (서버는 비교만 대문자, 저장은 원문).
export function normalizeVin(v) { return str(v).toUpperCase(); }

/// 주민/법인번호 전송값 'YYMMDD-NNNNNNN'. 뒷자리가 비었거나 마스킹('*')이면 'YYMMDD-' —
/// 마스킹값을 그대로 보내면 서버 forCreate 가 '' 로 저장한다(utils/ownerId.js).
/// 앞자리가 없으면 '' — 뒷자리만 보내면 서버가 판별 불가 원문을 그대로 암호화 저장한다.
/// 화면은 ownerIdComplete 게이트를 통과한 카드만 보내므로 실제 전송값은 항상 13자리다.
export function ownerIdFor(front, back) {
  var f = str(front);
  var b = str(back);
  if (!f) return '';
  if (b.indexOf('*') >= 0) b = '';
  return f + '-' + b;
}

/// HEIC/HEIF — /ocr 에 정규화가 없어 선택 단계에서 거부한다.
export function isHeic(file) {
  var type = str(file && file.type).toLowerCase();
  return type === 'image/heic' || type === 'image/heif' || /\.hei[cf]$/i.test(str(file && file.name));
}

export function isPdf(file) {
  return str(file && file.type).toLowerCase() === 'application/pdf' || /\.pdf$/i.test(str(file && file.name));
}

/// 파일 1장 = 카드 1장(차 1대). ocr: 'reading' | 'done' | 'failed'.
export function newCard(key, file, url) {
  return { key: key, file: file, url: url || '', ocr: 'reading', carNo: '', vin: '', carType: '', regDate: '', mileage: '',
    owner: '', ownerFront: '', ownerFixed: false, ownerBack: '', ownerAddr: '', weak: [], exists: false };
}

/// OCR data 병합 — 빈 칸만 채운다(먼저 입력한 칸은 그대로, 앱 mergeOcr). ownerId '800101-*******' → 앞 6 고정.
export function mergeOcr(card, d) {
  var next = Object.assign({}, card, { ocr: 'done' });
  ['carNo', 'vin', 'carType', 'regDate', 'owner', 'ownerAddr'].forEach(function (k) {
    if (!str(card[k])) next[k] = str(d[k]);
  });
  if (!str(card.mileage)) next.mileage = mileageText(d.mileage);
  var front = str(d.ownerId).split('-')[0].trim();
  if (!str(card.ownerFront) && front) { next.ownerFront = front; next.ownerFixed = true; }
  var conf = d.confidence || {};
  next.weak = CAR_FIELDS.filter(function (f) { return WEAK[conf[f]] && !fieldValue(card, f); });
  return next;
}

function fieldValue(card, f) {
  return f === 'ownerId' ? str(card.ownerFront) + str(card.ownerBack) : str(card[f]);
}

/// 칸 편집 — 그 칸의 OCR 약한 표시를 지우고, VIN 이 바뀌면 '이미 등록됨' 표시도 지운다(같은 VIN 이면 유지).
export function editField(card, field, value) {
  var next = Object.assign({}, card);
  next[field] = value;
  var f = field === 'ownerFront' || field === 'ownerBack' ? 'ownerId' : field;
  next.weak = card.weak.filter(function (w) { return w !== f; });
  if (field === 'vin' && normalizeVin(value) !== normalizeVin(card.vin)) next.exists = false;
  return next;
}

/// 부가서비스 칩 1개 → services 항목 (앱 ExtraServicePicker.selections: reissue 만 kind 동봉, 메모 칸 없음).
export function serviceEntry(option) {
  return option.kind === 'reissue' ? { serviceId: option.id, kind: 'reissue' } : { serviceId: option.id };
}

/// 주민/법인번호 숫자 13자리 — 말소신청서 필수(대행사 확인). 신청 버튼 게이트와 노란 칸이 같은 기준을 쓴다.
/// 실제 전송값(ownerIdFor)으로 판정 — 앞칸 공란·뒷칸 '*' 처럼 전송 단계에서 비는 입력이 게이트를 통과하지 않게(서버 classify 'rrn13' 와 동치).
export function ownerIdComplete(card) {
  return ownerIdFor(card.ownerFront, card.ownerBack).replace(/\D/g, '').length === 13;
}

/// 아직 안 채운 필수 칸 — 주민/법인번호는 13자리, 주행거리는 1 이상. 신청 버튼 게이트·노란 칸 공용(앱 missingRequired).
export function missingRequired(card) {
  return REQUIRED_FIELDS.filter(function (f) {
    if (f === 'ownerId') return !ownerIdComplete(card);
    if (f === 'mileage') return !mileageText(card.mileage);
    return !str(card[f]);
  });
}

/// 노란 칸 — OCR 약한 칸 + 아직 안 채운 필수 칸 (앱 flaggedFields).
export function flaggedFields(card) {
  var out = card.weak.slice();
  missingRequired(card).forEach(function (f) { if (out.indexOf(f) < 0) out.push(f); });
  return out;
}

function carJson(card, ov, exportCancel, broker, visit) {
  var o = { vin: normalizeVin(card.vin), carNo: str(card.carNo), carType: str(card.carType), regDate: str(card.regDate),
    mileage: Number(mileageText(card.mileage)) || 0, owner: str(card.owner), ownerId: ownerIdFor(card.ownerFront, card.ownerBack), ownerAddr: str(card.ownerAddr) };
  if (has(ov, 'services')) o.services = ov.services;
  if (exportCancel && has(ov, 'buyerName')) o.buyerName = str(ov.buyerName);
  if (broker) {
    var unitId = Number((broker.unitOf || {})[card.key]) || 0;
    var p = (broker.pickupOf || {})[unitId];
    if (unitId > 0) o.unitId = unitId;   // '(본인)' 도 보낸다 — 서버가 본인 몫은 0 으로 저장
    if (!visit && p && (p.kind === 'saved' || p.kind === 'custom')) o.pickupAddress = str(p.address);
  }
  return o;
}

/// 요청 body — 앱 MalsoQuickRequest.toJson 과 키·순서 동일.
/// common: { cancelType, intakeMethod, urgent, memo, pickupAddress, services[], buyerName }
/// overrides: { [card.key]: { services?, buyerName? } } — 키가 있는 차만 예외.
/// broker(대행사만): { unitOf:{[card.key]:unitId}, pickupOf:{[unitId]:{kind,address}} } — 차마다 unitId, 묶음이 저장 주소·다른 주소면
/// 그 차 pickupAddress(방문 접수는 안 붙인다). 없으면 현행 body 와 같다. 공통 pickupAddress 는 그대로(모아서 수거 차의 주소).
export function buildQuickBody(cards, common, overrides, broker) {
  var exp = common.cancelType !== '폐차말소';
  var visit = common.intakeMethod === '방문';
  var body = { cancelType: common.cancelType, intakeMethod: common.intakeMethod, isUrgent: common.urgent ? 1 : 0, memo: str(common.memo) };
  var pickup = visit ? '' : str(common.pickupAddress);
  if (pickup) body.pickupAddress = pickup;
  body.services = common.services || [];
  if (exp && str(common.buyerName)) body.buyerName = str(common.buyerName);
  body.cars = cards.map(function (c) { return carJson(c, (overrides || {})[c.key], exp, broker, visit); });
  return body;
}

// ── 대행사 거래처 (W1a) — 차마다 거래처를 고르고, 거래처 묶음마다 수거 주소를 고른다 ──

/// 고르기 시트 순서 — 본인 맨 위, 나머지는 최근 신청일 내림차순(빈 값 뒤, 같으면 서버 순서).
export function sortPickClients(clients) {
  return (clients || []).map(function (c, i) { return { c: c, i: i }; }).sort(function (a, b) {
    if (!!a.c.isSelf !== !!b.c.isSelf) return a.c.isSelf ? -1 : 1;
    var da = str(a.c.lastRequestDate), db = str(b.c.lastRequestDate);
    if (da !== db) return !da ? 1 : !db ? -1 : (da < db ? 1 : -1);
    return a.i - b.i;
  }).map(function (x) { return x.c; });
}

/// '최근' 배지 — 본인 외 최근 신청일이 가장 늦은 1곳(같으면 앞), 없으면 0.
export function recentClientId(clients) {
  var best = null;
  (clients || []).forEach(function (c) {
    var d = str(c.lastRequestDate);
    if (!c.isSelf && d && (!best || d > str(best.lastRequestDate))) best = c;
  });
  return best ? best.id : 0;
}

/// 짧은 주소 — 앞 두 마디 ("강원 동해시 해안로 55" → "강원 동해시").
export function shortAddr(addr) { return str(addr).split(/\s+/).slice(0, 2).join(' '); }

/// 묶음 수거 주소 기본값 — 본인 → 모아서 · 저장 주소 있음 → 저장 주소(값 복사) · 그 외 → 모아서.
export function defaultPickup(client) {
  var addr = str(client && client.pickupAddress);
  return client && !client.isSelf && addr ? { kind: 'saved', address: addr } : { kind: 'collect', address: '' };
}

/// 이 차에 고른 거래처 — unitOf 의 id 가 지금 목록에 있을 때만 그 거래처, 아니면 null(미선택).
/// 행 표시·[다음] 잠금·'안 고른 차에도 같이 넣기'가 모두 이 판정을 쓴다(다시 읽은 목록에서 빠진 거래처 = 미선택).
export function pickedClient(clients, unitOf, key) {
  var id = Number((unitOf || {})[key]) || 0;
  return id ? (clients || []).filter(function (c) { return c.id === id; })[0] || null : null;
}

/// 확인 단계 묶음 — [{unitId, client, cards[]}], 본인 묶음 먼저, 나머지는 차 순서상 첫 등장 순. 거래처 없는 카드는 빠진다.
export function groupCards(cards, unitOf, clients) {
  var out = [];
  (cards || []).forEach(function (card) {
    var id = Number((unitOf || {})[card.key]) || 0;
    if (!id) return;
    var g = out.filter(function (x) { return x.unitId === id; })[0];
    if (!g) {
      g = { unitId: id, client: (clients || []).filter(function (c) { return c.id === id; })[0] || { id: id, name: '' }, cards: [] };
      out.push(g);
    }
    g.cards.push(card);
  });
  return out.filter(function (g) { return g.client.isSelf; }).concat(out.filter(function (g) { return !g.client.isSelf; }));
}

/// 신청 게이트 — '다른 주소 입력' 묶음의 주소가 비면 false. 방문 접수는 수거 주소를 쓰지 않으므로 true.
export function brokerPickupReady(groups, pickupOf, intakeMethod) {
  if (intakeMethod === '방문') return true;
  return (groups || []).every(function (g) {
    var p = (pickupOf || {})[g.unitId];
    return !p || p.kind !== 'custom' || !!str(p.address);
  });
}

/// 전송 전 차단 — 빈 VIN · 요청 안 중복 VIN. 문제 없으면 null.
export function precheckVins(cards) {
  var vins = cards.map(function (c) { return normalizeVin(c.vin); });
  if (vins.indexOf('') >= 0) return { key: 'malsoModeVinRequired' };
  var dups = vins.filter(function (v, i) { return vins.indexOf(v) !== i; })
    .filter(function (v, i, a) { return a.indexOf(v) === i; });
  return dups.length ? { key: 'malsoModeErrVinDuplicate', vars: { vins: dups.join(', ') } } : null;
}

/// quick 실패 → { key, vars?, existing?, index?, unitIndex? }. 매핑 없는 code 는 null(호출측 describeSimpleError).
/// UNIT_NOT_FOUND 의 unitIndex 는 0부터인 카드 위치 — 화면이 그 카드의 거래처를 비우고 ① 로 돌아간다.
/// 서버 부가 필드는 err.body(api.js 정규화) — 화면에는 문구 키·VIN 목록·차 위치만 넘긴다(body 원문 노출 금지).
/// OWNER_ID_REQUIRED · FIELD_REQUIRED 의 index 는 0부터인 카드 위치, field 는 그 카드에서 노랗게 할 칸.
export function mapQuickError(err) {
  var e = err || {};
  var b = e.body || e;
  var index = (Number(b.index) || 0) + 1;
  switch (e.code) {
    case 'AGENCY_NOT_RESOLVED': return { key: 'malsoModeErrAgencyNotResolved' };
    case 'NO_CARS': return { key: 'malsoModeErrNoCars' };
    case 'VIN_REQUIRED': return { key: 'malsoModeErrVinRequired', vars: { index: index } };
    case 'VIN_DUPLICATE_IN_REQUEST': return { key: 'malsoModeErrVinDuplicate', vars: { vins: (b.vins || []).join(', ') } };
    case 'VIN_EXISTS': return { key: 'malsoModeErrVinExists', existing: (b.existing || []).map(function (x) { return normalizeVin(x && x.vin); }) };
    case 'SERVICE_INVALID': return { key: 'malsoModeErrServiceInvalid', vars: { index: index } };
    case 'OWNER_ID_REQUIRED': return { key: 'malsoModeOwnerIdRequired', index: index - 1, field: 'ownerId' };
    case 'FIELD_REQUIRED': return { key: 'malsoModeRequiredMissing', index: index - 1, field: str(b.field) };
    case 'UNIT_NOT_FOUND': return { key: 'malsoModeErrUnitNotFound', vars: { index: index }, unitIndex: index - 1 };
    default: return null;
  }
}
